// =============================================================================
// Convite "Instalar o app" do portal do aluno — regras PURAS (testáveis).
//
// Pedido do dono (28/09/2026): assim que alguém entrar no rotina.bio, oferecer
// a instalação do app do aluno. O "app" é o próprio portal instalado como PWA
// (atalho na tela inicial, abre em tela cheia no /s/<slug> do aluno):
//  - Android/Chrome (e Edge/Samsung): usa o instalador nativo
//    (`beforeinstallprompt` → `prompt()`); se o evento não vier, mostra o
//    caminho pelo menu do navegador.
//  - iPhone/iPad: não existe instalador programático — mostra o passo a passo
//    do "Adicionar à Tela de Início".
//  - Computador: só se o navegador oferecer o instalador nativo.
// Nunca aparece: já instalado (display-mode standalone), dentro de iframe (o
// "Ver como aluno" do trainer), na prévia, ou até 7 dias depois de o aluno
// dispensar. Depois de instalado (evento do navegador, ou "Já instalei" no
// iPhone — lá o app não divide armazenamento com o Safari) não volta mais.
// =============================================================================

export type InstallPlatform = "ios" | "android" | "desktop";

/** Dias de silêncio depois de "Agora não" / "Entendi". */
export const INSTALL_PROMPT_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

/** Quanto esperar o instalador nativo no Android antes de mostrar o caminho manual. */
export const ANDROID_NATIVE_PROMPT_WAIT_MS = 3000;

/**
 * Plataforma pelo user agent. iPadOS 13+ se apresenta como Mac ("Macintosh")
 * — a diferença é ter tela de toque (`maxTouchPoints > 1`).
 */
export function detectPlatform(userAgent: string, maxTouchPoints = 0): InstallPlatform {
  const ua = userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Macintosh/i.test(ua) && maxTouchPoints > 1) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

/**
 * Navegador embutido de app (Instagram, Facebook, TikTok, LINE, WeChat...):
 * não instala nada — o aluno precisa abrir o link no Safari/Chrome.
 */
export function isInAppBrowser(userAgent: string): boolean {
  return /FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|TikTok|musical_ly|Snapchat|Pinterest\//i.test(
    userAgent || "",
  );
}

/**
 * Slug do aluno na URL (`/s/<slug>/...`), em minúsculas — o Worker e o portal
 * tratam o slug assim (o AppShell redireciona /s/AbC → /s/abc depois do login).
 * null fora do portal.
 */
export function slugFromPath(pathname: string): string | null {
  const m = /^\/s\/([^/?#]+)/.exec(pathname || "");
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]!).toLowerCase();
  } catch {
    return null;
  }
}

/** Rota da prévia do trainer — nunca convida a instalar. */
export function isPreviewPath(pathname: string, search = ""): boolean {
  return /\/preview-bridge(?:\/|$)/.test(pathname || "") || /(?:^|[?&])preview=1(?:&|$)/.test(search || "");
}

export interface InstallPromptMemory {
  /** Quando o aluno dispensou por último (epoch ms). */
  dismissedAt?: number;
  /** O app foi instalado (evento `appinstalled` ou prompt aceito). */
  installed?: boolean;
}

export function memoryKey(slug: string): string {
  return `portal.installApp.${slug}`;
}

/** Lê a memória do convite. Storage bloqueado (aba anônima, Safari privado) = memória vazia. */
export function readMemory(storage: Pick<Storage, "getItem"> | null | undefined, slug: string): InstallPromptMemory {
  try {
    const raw = storage?.getItem(memoryKey(slug));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as InstallPromptMemory;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** Grava a memória do convite (falha de storage é ignorada — no pior caso o convite reaparece). */
export function writeMemory(
  storage: Pick<Storage, "getItem" | "setItem"> | null | undefined,
  slug: string,
  patch: InstallPromptMemory,
): void {
  try {
    const next = { ...readMemory(storage, slug), ...patch };
    storage?.setItem(memoryKey(slug), JSON.stringify(next));
  } catch {
    // Storage indisponível: segue sem memória.
  }
}

export interface OfferContext {
  /** Já rodando instalado (display-mode standalone / navigator.standalone). */
  standalone: boolean;
  /** Dentro de iframe (prévia "Ver como aluno" no Kyra). */
  inIframe: boolean;
  /** Rota/URL de prévia do trainer. */
  preview: boolean;
  memory: InstallPromptMemory;
  now: number;
  /** Aberto pelo aluno (botão "Instalar app" no Perfil): ignora o silêncio de 7 dias. */
  forced?: boolean;
}

/** Pode oferecer a instalação agora? */
export function shouldOfferInstall(ctx: OfferContext): boolean {
  if (ctx.standalone || ctx.inIframe || ctx.preview) return false;
  if (ctx.forced) return true;
  if (ctx.memory.installed) return false;
  const dismissedAt = Number(ctx.memory.dismissedAt);
  if (Number.isFinite(dismissedAt) && dismissedAt > 0 && ctx.now - dismissedAt < INSTALL_PROMPT_SNOOZE_MS) {
    return false;
  }
  return true;
}

export type InstallMode =
  /** Instalador nativo disponível: botão "Instalar app". */
  | "native"
  /** iPhone/iPad: passo a passo do Compartilhar → Adicionar à Tela de Início. */
  | "ios"
  /** Android sem instalador nativo: caminho pelo menu do navegador. */
  | "android-manual"
  /** App com navegador embutido: abrir no Safari/Chrome. */
  | "in-app"
  /** Nada a oferecer (computador sem instalador nativo). */
  | "none";

/**
 * Qual convite mostrar. `nativeAvailable` = o `beforeinstallprompt` chegou;
 * `nativeWaitElapsed` = já esperamos o suficiente por ele no Android.
 */
export function pickInstallMode(input: {
  platform: InstallPlatform;
  inAppBrowser: boolean;
  nativeAvailable: boolean;
  nativeWaitElapsed: boolean;
}): InstallMode {
  if (input.nativeAvailable) return "native";
  if (input.inAppBrowser) return input.platform === "desktop" ? "none" : "in-app";
  if (input.platform === "ios") return "ios";
  if (input.platform === "android") return input.nativeWaitElapsed ? "android-manual" : "none";
  return "none";
}
